import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, Grid, LinearProgress, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, StatusChip, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

const money = (n) => `₹${(parseFloat(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReimbursementReportNew() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/reimbursement/logineduser/get-reimbursement-detail");
        setRows(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        toast.error(extractErrorMessage(err));
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (fromDate && new Date(r.fromDate) < new Date(fromDate)) return false;
      if (toDate && new Date(r.toDate) > new Date(toDate)) return false;
      return true;
    });
  }, [rows, fromDate, toDate]);

  const summary = useMemo(() => {
    let total = 0, approved = 0, pending = 0, rejected = 0, items = 0;
    filtered.forEach((r) => {
      (r.expenses || []).forEach((e) => {
        const amt = parseFloat(e.total) || 0;
        total += amt;
        items += 1;
        const st = (e.status || "pending").toLowerCase();
        if (st === "approved" || st === "paid") approved += amt;
        else if (st === "rejected") rejected += amt;
        else pending += amt;
      });
    });
    return { total, approved, pending, rejected, items };
  }, [filtered]);

  const exportExcel = async () => {
    try {
      const XLSX = await import("xlsx");
      const data = [];
      filtered.forEach((r) => {
        (r.expenses || []).forEach((e) => {
          data.push({
            Employee: r.employeeName,
            Purpose: r.purpose || "",
            From: r.fromDate ? new Date(r.fromDate).toLocaleDateString("en-IN") : "",
            To: r.toDate ? new Date(r.toDate).toLocaleDateString("en-IN") : "",
            "Expense Date": e.date ? new Date(e.date).toLocaleDateString("en-IN") : "",
            Description: e.description || "",
            Lodging: parseFloat(e.lodging) || 0,
            Boarding: parseFloat(e.boarding) || 0,
            Food: parseFloat(e.food) || 0,
            Conveyance: parseFloat(e.conveyance) || 0,
            Material: parseFloat(e.Material_Purchase) || 0,
            PO: parseFloat(e.PO) || 0,
            Invoice: parseFloat(e.Invoice) || 0,
            Other: parseFloat(e.other) || 0,
            Total: parseFloat(e.total) || 0,
            Status: e.status || "pending",
            Note: e.note || "",
          });
        });
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reimbursements");
      XLSX.writeFile(wb, `reimbursements-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Excel exported");
    } catch (err) {
      toast.error("Excel export failed - xlsx package missing");
    }
  };

  const downloadReceipts = async () => {
    const urls = [];
    filtered.forEach((r) => (r.expenses || []).forEach((e) => (e.files || []).forEach((f) => urls.push(f))));
    if (urls.length === 0) return toast.info("No receipts to download");
    setDownloading(true);
    setProgress(0);
    try {
      const JSZip = (await import("jszip")).default;
      const { saveAs } = await import("file-saver");
      const zip = new JSZip();
      let done = 0;
      await Promise.all(urls.map(async (u, i) => {
        try {
          const res = await fetch(u);
          const blob = await res.blob();
          zip.file(`receipt-${i + 1}-${u.split("/").pop()}`, blob);
        } catch {}
        done += 1;
        setProgress(Math.round((done / urls.length) * 100));
      }));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `receipts-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success(`Downloaded ${urls.length} receipts`);
    } catch (err) {
      toast.error("ZIP download failed");
    } finally {
      setDownloading(false);
      setProgress(0);
    }
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Reimbursement Report"
        subtitle="Generate summaries and download receipts"
        icon={<SummarizeOutlinedIcon />}
        actions={[
          <Button key="excel" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportExcel}>Excel</Button>,
          <Button key="zip" variant="contained" startIcon={<FolderZipOutlinedIcon />} onClick={downloadReceipts} disabled={downloading}>
            {downloading ? "Zipping…" : "Download ZIP"}
          </Button>,
        ]}
      />

      {downloading && (
        <Box mb={2}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption">Preparing archive</Typography>
            <Typography variant="caption">{progress}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      )}

      <SectionCard accent="#6C50BF" sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField fullWidth size="small" type="date" label="From Date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField fullWidth size="small" type="date" label="To Date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} inputProps={{ min: fromDate }} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="outlined" onClick={() => { setFromDate(""); setToDate(""); }}>Clear</Button>
          </Grid>
        </Grid>
      </SectionCard>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}><StatCard label="Total Claims" value={summary.items} hint={`${filtered.length} requests`} accent="#6C50BF" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Total Amount" value={money(summary.total)} hint="In range" accent="#39C645" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Approved / Paid" value={money(summary.approved)} hint="Settled" accent="#5C85FF" /></Grid>
        <Grid item xs={6} md={3}><StatCard label="Pending" value={money(summary.pending)} hint="Awaiting review" accent="#F9BC06" /></Grid>
      </Grid>

      <SectionCard title="Itemized Expenses" subtitle={`${summary.items} line items`}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <EmptyState title="No data" subtitle="No reimbursements in this date range." />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Purpose</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.flatMap((r) =>
                  (r.expenses || []).map((e) => (
                    <TableRow key={e._id || `${r._id}-${e.date}`} hover>
                      <TableCell sx={{ fontSize: 13 }}>{e.date ? new Date(e.date).toLocaleDateString("en-IN") : "—"}</TableCell>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{r.employeeName}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{e.description || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>{r.purpose || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>{money(e.total)}</TableCell>
                      <TableCell><StatusChip status={e.status || "pending"} /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
}
